import type { ApiException } from '@/common/error';
import { isOwnerGroupRole, UserRole } from '@/utils/roles';
import { formatIsoToDateSlash, formatIsoToDateTimeSlash } from '@/utils/timeFormatter';
import {
  addCalendarDays,
  dayInTimeZone,
  formatTimeZoneLabel,
  getBrowserTimeZone,
  zonedMidnight,
} from '@/utils/timezone';

import type { ContractDaysDto, ContractDto, TemporaryContractDto } from './contracts.dto';

export interface Contract {
  /** ISO, UTC: the vessel's midnight on the first day. */
  startAt: string;
  /** ISO, UTC: the vessel's midnight after the last day, when the contract stops. */
  endAt: string;
  daysLeft: number;
  shorteningAllowed: boolean;
}

export interface TemporaryContract {
  startAt: string;
  endAt: string;
  permissions?: string[];
}

export const mapContract = (dto?: ContractDto | null): Contract | undefined =>
  dto
    ? {
        startAt: dto.start_at,
        endAt: dto.end_at,
        daysLeft: dto.days_left,
        shorteningAllowed: dto.shortening_allowed,
      }
    : undefined;

export const mapTemporaryToDto = (
  contract?: TemporaryContract,
): TemporaryContractDto | undefined =>
  contract
    ? {
        start_at: contract.startAt,
        end_at: contract.endAt,
        permissions: contract.permissions,
      }
    : undefined;

/** When a contract starting on `firstDay` (`YYYY-MM-DD`, vessel calendar) starts: ISO, UTC. */
export const contractStartAt = (firstDay: string, vesselTimeZone: string): string =>
  zonedMidnight(firstDay, vesselTimeZone).toISOString();

/** When a contract whose last day is `lastDay` stops: the vessel's midnight after it, ISO, UTC. */
export const contractEndAt = (lastDay: string, vesselTimeZone: string): string =>
  zonedMidnight(addCalendarDays(lastDay, 1), vesselTimeZone).toISOString();

/** The contract's first day on the vessel's calendar, `YYYY-MM-DD`. */
export const contractFirstDay = (contract: Contract, vesselTimeZone: string): string =>
  dayInTimeZone(new Date(contract.startAt), vesselTimeZone);

/** The contract's last day on the vessel's calendar: the moment before it stops. */
export const contractLastDay = (contract: Contract, vesselTimeZone: string): string =>
  dayInTimeZone(new Date(new Date(contract.endAt).getTime() - 1), vesselTimeZone);

/**
 * A contract day as people read it, `MM/DD/YYYY`. The value is already the vessel's calendar
 * day; formatting it in the browser zone would move it a day across the Americas.
 */
export const formatContractDay = (day: string): string =>
  formatIsoToDateSlash(day, { timeZone: 'UTC' });

/** Ends within a week, today included: the roster and the member page warn about it. */
export const CONTRACT_ENDING_SOON_DAYS = 7;

export const contractEndingSoon = (contract: Contract): boolean =>
  contract.daysLeft <= CONTRACT_ENDING_SOON_DAYS;

/**
 * What every contract date picker says under it: the days are the vessel's, and a contract runs
 * to the end of its last one. A viewer in another zone is told so, because "today" may already
 * be tomorrow or still yesterday on board, and once a last day is picked, when that is for them.
 */
export const contractTimezoneNote = (vesselTimeZone: string, lastDay?: string): string => {
  const note = `Dates are in the vessel's timezone — ${formatTimeZoneLabel(vesselTimeZone)}. The contract ends at the end of the last day (vessel time).`;
  const deviceTimeZone = getBrowserTimeZone();

  if (deviceTimeZone === vesselTimeZone) return note;

  const device = `${note} Your device is set to ${formatTimeZoneLabel(deviceTimeZone)}`;
  if (!lastDay) return `${device}.`;

  const endsAt = formatIsoToDateTimeSlash(contractEndAt(lastDay, vesselTimeZone), {
    timeZone: deviceTimeZone,
  });

  return `${device}, where it ends on ${endsAt}.`;
};

const CONTRACT_ERRORS: Record<string, string> = {
  TEMPORARY_ROLE: 'Only crew roles can be hired on a temporary contract',
  INVALID_CONTRACT_DATES: "The last day can't be before the first",
  CONTRACT_IN_PAST: "The contract can't start before today or end in the past (vessel time)",
  NOT_OPEN_TO_TEMPORARY: "This person isn't open to temporary contracts",
  PERMISSION_NOT_GRANTABLE: 'One of the permissions can only be held by the owner',
  CONTRACT_SHORTENING_DISABLED: 'A contract can only be extended',
  CONTRACT_UNCHANGED: 'The contract already ends on that day',
  CONTRACT_CHANGED: 'The contract changed meanwhile. Reload and try again',
  NOT_CONTRACT_MANAGER: "You can't change this contract",
  NOT_TEMPORARY: 'This crew member has no temporary contract',
  CONTRACT_ENDED: 'The contract this invitation offered has already ended',
  INVITE_ALREADY_ACCEPTED: "You've already accepted. You'll join when the contract starts",
};

/** The rule a temporary hire or a contract change ran into, or `fallback` for anything else. */
export const contractErrorMessage = (error: unknown, fallback: string): string => {
  const type = (error as Partial<ApiException> | null)?.type;
  return (type && CONTRACT_ERRORS[type]) || fallback;
};

/**
 * Whether `role` can be hired on a temporary contract by someone holding `hirerRole`: crew roles
 * and captain, never the owner group, a guest or a service provider, and a captain only by the
 * owner group or a captain. Mirrors the people service, which refuses anything else.
 */
export const temporaryHireAllowed = (role: string, hirerRole: string): boolean =>
  !isOwnerGroupRole(role) &&
  role !== UserRole.GUEST &&
  role !== UserRole.SERVICE_PROVIDER &&
  (role !== UserRole.CAPTAIN || isOwnerGroupRole(hirerRole) || hirerRole === UserRole.CAPTAIN);

/**
 * An invitation's contract as the invitee reads it: the vessel's days when the service sent them,
 * otherwise the days in this device's timezone.
 */
export const invitationContractDays = (
  contract: Contract,
  days?: ContractDaysDto,
): { firstDay: string; lastDay: string } =>
  days
    ? { firstDay: days.first_day, lastDay: days.last_day }
    : {
        firstDay: contractFirstDay(contract, getBrowserTimeZone()),
        lastDay: contractLastDay(contract, getBrowserTimeZone()),
      };

/** Whether a contract has yet to start: accepting it only books the start. */
export const contractNotStarted = (contract: Contract, now = Date.now()): boolean =>
  new Date(contract.startAt).getTime() > now;
